import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getAvailableSlots, publicBookAppointment } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, User, Phone, Mail, Check, ChevronLeft, ChevronRight, Stethoscope } from 'lucide-react';

export default function PublicBooking() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: date, 2: time, 3: info, 4: confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    paternal_surname: '',
    maternal_surname: '',
    phone: '',
    email: '',
    reason: '',
  });

  const handlePhoneChange = (e) => {
    // Solo permitir números y máximo 10 dígitos
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  // Generate next 14 days
  const getDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const fetchSlots = useCallback(async (date) => {
    try {
      setIsLoading(true);
      const response = await getAvailableSlots(formatDate(date));
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setAvailableSlots(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error('Error al cargar horarios disponibles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchSlots(selectedDate);
    }
  }, [selectedDate, fetchSlots]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.paternal_surname || !formData.phone) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    
    if (formData.phone.length !== 10) {
      toast.error('El teléfono debe tener exactamente 10 dígitos');
      return;
    }
    
    setIsLoading(true);
    try {
      await publicBookAppointment({
        first_name: formData.first_name,
        paternal_surname: formData.paternal_surname,
        maternal_surname: formData.maternal_surname || null,
        phone: formData.phone,
        email: formData.email || null,
        date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        reason: formData.reason || null,
      });
      
      setBookingComplete(true);
      setStep(4);
      toast.success('¡Cita agendada exitosamente!');
    } catch (error) {
      let errorMessage = 'Error al agendar la cita';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0];
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-slate-100 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-[#588157] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] mb-2">
              ¡Cita Confirmada!
            </h2>
            <p className="text-[#5D5C61] mb-6">
              Tu cita ha sido agendada exitosamente.
            </p>
            
            <div className="bg-[#F4F1EA] rounded-lg p-4 text-left mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#335C67]" />
                <span className="font-medium text-[#2B2D42]">
                  {selectedDate && formatDisplayDate(selectedDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#335C67]" />
                <span className="text-[#5D5C61]">
                  {selectedSlot?.start_time} - {selectedSlot?.end_time}
                </span>
              </div>
            </div>
            
            <p className="text-sm text-[#8D99AE] mb-4">
              Recibirás un recordatorio por WhatsApp antes de tu cita.
            </p>
            
            <Button
              className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              onClick={() => window.location.href = '/portal'}
            >
              Acceder a mi portal
            </Button>
          </CardContent>
        </Card>
        </div>
        <footer className="py-4 px-6 border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-[#8D99AE]">
              Desarrollado por{' '}
              <a
                href="https://agsoftweb.com.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
              >
                Agsoftweb
              </a>
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#335C67] rounded-xl flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-[#2B2D42] font-['Manrope']">Consulterest</h1>
              <p className="text-xs text-[#8D99AE]">Agendar Cita</p>
            </div>
          </div>
          <Link
            to="/login"
            className="text-sm text-[#335C67] hover:text-[#244048] font-medium"
          >
            Iniciar sesión
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                step >= s ? 'bg-[#335C67]' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Select Date */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">
              Selecciona una fecha
            </h2>
            <p className="text-[#5D5C61] text-center mb-8">
              Elige el día que prefieras para tu consulta
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {getDates().map((date) => {
                const isToday = formatDate(date) === formatDate(new Date());
                return (
                  <Card
                    key={formatDate(date)}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedDate && formatDate(selectedDate) === formatDate(date)
                        ? 'border-[#335C67] bg-[#335C67]/5'
                        : 'border-slate-100 hover:border-[#335C67]/30'
                    }`}
                    onClick={() => handleDateSelect(date)}
                    data-testid={`date-${formatDate(date)}`}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-[#8D99AE] uppercase">
                        {date.toLocaleDateString('es-MX', { weekday: 'short' })}
                      </p>
                      <p className="text-2xl font-bold text-[#2B2D42] my-1">
                        {date.getDate()}
                      </p>
                      <p className="text-xs text-[#5D5C61]">
                        {date.toLocaleDateString('es-MX', { month: 'short' })}
                      </p>
                      {isToday && (
                        <Badge className="mt-2 bg-[#E09F3E] text-xs">Hoy</Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && (
          <div className="animate-fade-in">
            <Button
              variant="ghost"
              className="mb-4"
              onClick={() => setStep(1)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar fecha
            </Button>
            
            <h2 className="text-2xl font-bold text-[#2B2D42] font-['Manrope'] text-center mb-2">
              Selecciona un horario
            </h2>
            <p className="text-[#5D5C61] text-center mb-2">
              {selectedDate && formatDisplayDate(selectedDate)}
            </p>
            <p className="text-sm text-[#8D99AE] text-center mb-8">
              Horarios disponibles
            </p>
            
            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#5D5C61]">Cargando horarios...</p>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                {availableSlots.map((slot, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className={`h-auto py-4 ${
                      selectedSlot === slot
                        ? 'border-[#335C67] bg-[#335C67] text-white'
                        : 'border-slate-200 hover:border-[#335C67]'
                    }`}
                    onClick={() => handleSlotSelect(slot)}
                    data-testid={`slot-${slot.start_time}`}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {slot.start_time}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#2B2D42]">Sin disponibilidad</h3>
                <p className="text-[#5D5C61] mt-1">No hay horarios disponibles para esta fecha</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setStep(1)}
                >
                  Elegir otra fecha
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Patient Info */}
        {step === 3 && (
          <div className="animate-fade-in max-w-md mx-auto">
            <Button
              variant="ghost"
              className="mb-4"
              onClick={() => setStep(2)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cambiar horario
            </Button>
            
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-xl font-['Manrope']">Tus datos</CardTitle>
                <CardDescription>
                  Completa tu información para confirmar la cita
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Selected slot summary */}
                <div className="bg-[#F4F1EA] rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-[#335C67]" />
                    <span className="font-medium text-[#2B2D42]">
                      {selectedDate && formatDisplayDate(selectedDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#335C67]" />
                    <span className="text-[#5D5C61]">
                      {selectedSlot?.start_time} - {selectedSlot?.end_time}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#8D99AE]" />
                      Nombre(s) *
                    </Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Juan Carlos"
                      data-testid="booking-first-name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Apellido Paterno *</Label>
                      <Input
                        value={formData.paternal_surname}
                        onChange={(e) => setFormData({ ...formData, paternal_surname: e.target.value })}
                        placeholder="Pérez"
                        data-testid="booking-paternal-surname"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Apellido Materno</Label>
                      <Input
                        value={formData.maternal_surname}
                        onChange={(e) => setFormData({ ...formData, maternal_surname: e.target.value })}
                        placeholder="García"
                        data-testid="booking-maternal-surname"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#8D99AE]" />
                      Teléfono (10 dígitos) *
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="8123456789"
                      data-testid="booking-phone"
                      required
                      maxLength={10}
                      type="tel"
                    />
                    <p className="text-xs text-[#8D99AE]">Ingresa solo los 10 dígitos (sin LADA). Recibirás confirmación y recordatorios</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#8D99AE]" />
                      Correo (opcional)
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                      data-testid="booking-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Motivo de consulta (opcional)</Label>
                    <Input
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="Primera consulta, seguimiento, etc."
                      data-testid="booking-reason"
                    />
                  </div>


                  <Button
                    type="submit"
                    className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full h-12"
                    disabled={isLoading}
                    data-testid="booking-submit"
                  >
                    {isLoading ? 'Confirmando...' : 'Confirmar Cita'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      <footer className="mt-auto py-4 px-6 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-[#8D99AE]">
            Desarrollado por{' '}
            <a
              href="https://agsoftweb.com.mx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
            >
              Agsoftweb
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
